import { isAmplitudeUrl } from './amplitude-url';

// POC: Amplitude's success body; the SDK is assumed to treat this as delivered and not retry.
const FAKE_AMPLITUDE_SUCCESS_BODY = JSON.stringify({ code: 200 });

const getRequestUrl = (input: RequestInfo | URL): string =>
  typeof Request !== 'undefined' && input instanceof Request
    ? input.url
    : String(input);

const blockFetch = (onBlocked: (url: string) => void): void => {
  const originalFetch = window.fetch;
  window.fetch = function blockingFetch(input, init) {
    const url = getRequestUrl(input);
    if (isAmplitudeUrl(url)) {
      onBlocked(url);
      return Promise.resolve(
        new Response(FAKE_AMPLITUDE_SUCCESS_BODY, {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        }),
      );
    }
    return originalFetch.call(this, input, init);
  };
};

const blockSendBeacon = (onBlocked: (url: string) => void): void => {
  if (typeof navigator.sendBeacon !== 'function') {
    return;
  }
  const originalSendBeacon = navigator.sendBeacon.bind(navigator);
  navigator.sendBeacon = (url, data) => {
    if (isAmplitudeUrl(String(url))) {
      onBlocked(String(url));
      // true tells the caller the beacon was queued, so it doesn't fall back to another transport
      return true;
    }
    return originalSendBeacon(url, data);
  };
};

const blockXhr = (onBlocked: (url: string) => void): void => {
  const blockedXhrUrls = new WeakMap<XMLHttpRequest, string>();
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function blockingOpen(
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    ...rest: [boolean?, string?, string?]
  ) {
    if (isAmplitudeUrl(String(url))) {
      blockedXhrUrls.set(this, String(url));
    }
    return (originalOpen as (...args: unknown[]) => void).call(
      this,
      method,
      url,
      ...rest,
    );
  } as typeof XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.send = function blockingSend(body) {
    const blockedUrl = blockedXhrUrls.get(this);
    if (blockedUrl) {
      onBlocked(blockedUrl);
      // POC: the SDK sees an aborted request; whether it retries is unverified.
      this.abort();
      return;
    }
    originalSend.call(this, body);
  };
};

// Only requests made after this runs are blocked; anything sent before the wrapper loaded already went out.
export const startAmplitudeRequestBlocking = (
  onBlocked: (url: string) => void,
): void => {
  blockFetch(onBlocked);
  blockSendBeacon(onBlocked);
  blockXhr(onBlocked);
};
